import { Component, Input, OnInit } from '@angular/core';
import { LoadingController, ModalController } from '@ionic/angular';
import * as dayjs from 'dayjs';
import { GlobalConstants } from 'src/app/constant/constant';
import { NotificationsService } from 'src/app/services/notifications.service';
import { TimeRecordService } from 'src/app/services/timeRecord.service';
import { NotableActivityFormControlParams, NotableActivityFormDataValues } from '../notable-activity.component';
import { Activity } from 'src/app/entities/Activity';
import { DateTimeHelper } from 'src/app/utils/date-time-helper';
import { DataChangeEmitWho } from 'src/app/models/data-change-emit/data-change-emit-payload';
import { TimeService } from 'src/app/services/time.service';
import { ActivityService } from 'src/app/services/activity.service';
import { DeviceInfoService } from 'src/app/services/device-info.service';
import { TOAST_CLASS } from 'src/app/enums/notification/notification.enum';
import { ActionValidationException, NetworkException } from 'src/app/utils/exceptions';
import { TimeEntryIntervalResolverService } from 'src/app/services/time-entry-interval-resolver.service';
import { AppUserService } from 'src/app/services/app-user.service';

@Component({
  selector: 'app-edit-notable-activity',
  templateUrl: './edit-notable-activity.component.html',
  styleUrls: ['./edit-notable-activity.component.scss'],
})
export class EditNotableActivityComponent implements OnInit {
  INTERVAL = GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;

  @Input() userId: number;
  @Input() notableActivityToEditRecordId: number;
  @Input() interval: number | null = null;
  notableActivityToEdit: Activity;
  notableActivityFormControlParams: NotableActivityFormControlParams;
  timeZoneId: string;

  formValues: NotableActivityFormDataValues;

  constructor(
    private timeService: TimeService,
    private activityService: ActivityService,
    private timeRecordService: TimeRecordService,
    private notificationsService: NotificationsService,
    private loadingController: LoadingController,
    private modalController: ModalController,
    private deviceInfoService: DeviceInfoService,
    private timeEntryIntervalResolverService: TimeEntryIntervalResolverService,
    private appUserService: AppUserService
  ) {}

  async ngOnInit() {
    await this.loadActivity();
    await this.loadEffectiveInterval();
    await this.setFormParams();
  }

  private async loadEffectiveInterval() {
    if (this.interval !== null) {
      this.INTERVAL = this.interval;
      return;
    }
    this.INTERVAL = await this.timeEntryIntervalResolverService.resolveIntervalForDate(
      this.userId,
      this.notableActivityToEdit.UserSpecifiedDate,
      this.timeZoneId,
      this.appUserService.currentUser?.IsExempt
    );
  }

  async loadActivity() {
    this.notableActivityToEdit = await this.activityService.getActivityByRecordId(this.notableActivityToEditRecordId);
    if (!this.notableActivityToEdit) {
      this.notificationsService.showToast({ message: 'Activity not found.', cssClass: TOAST_CLASS.DANGER });
      await this.onClose();
    }
    this.timeZoneId = this.notableActivityToEdit.TimeZoneId;
  }

  onFormValuesChanged(formValues: NotableActivityFormDataValues) {
    this.formValues = formValues;
  }

  async setFormParams() {
    const minDateTime = (await this.timeRecordService.getMinClockInForDay(this.notableActivityToEdit.UserSpecifiedDate, this.userId)).tz(this.notableActivityToEdit.TimeZoneId);
    let maxDateTime: dayjs.Dayjs;

    const currentDateTime = DateTimeHelper.getLocalNowString();
    const roundedCurrentDatetime = DateTimeHelper.roundDateTime(currentDateTime, this.INTERVAL).tz(this.notableActivityToEdit.TimeZoneId);

    let prelimMaxDatetime: dayjs.Dayjs;

    if ((await this.timeRecordService.checkIsClockedIn(this.userId)) && DateTimeHelper.checkIsToday(this.notableActivityToEdit.UserSpecifiedDate)) {
      prelimMaxDatetime = roundedCurrentDatetime;
    } else {
      const lastTimeRecord = await this.timeRecordService.getLastestTimeRecordByUserIdDate(this.userId, this.notableActivityToEdit.UserSpecifiedDate);
      prelimMaxDatetime = dayjs(lastTimeRecord.ClockOut ?? '', lastTimeRecord.ClockInTimeZoneId);
    }

    const lastAllowedClockIn = this.timeRecordService.getLastAllowedClockInForDay(this.notableActivityToEdit.UserSpecifiedDate, this.INTERVAL).tz(this.notableActivityToEdit.TimeZoneId);
    if (prelimMaxDatetime.isBefore(lastAllowedClockIn)) {
      maxDateTime = prelimMaxDatetime;
    } else {
      maxDateTime = lastAllowedClockIn;
    }

    this.notableActivityFormControlParams = {
      notableActivityToEdit: this.notableActivityToEdit,
      dateTime: {
        initialValue: this.notableActivityToEdit.UserSpecifiedDate.toString(),
        minValue: DateTimeHelper.formatDateTime(minDateTime),
        maxValue: DateTimeHelper.formatDateTime(maxDateTime),
      },
    };
  }

  async presentLoading() {
    const loading = await this.loadingController.create({
      message: 'Saving...',
    });
    await loading.present();
  }

  async onClose() {
    await this.modalController.dismiss();
  }

  async onSave() {
    try {
      await this.presentLoading();

      await this.timeService.updateNotableActivity(
        DataChangeEmitWho.Self,
        this.notableActivityToEditRecordId,
        {
          ActivityDate: this.formValues.dateTime,
          Text: this.formValues.text,
          TimeZoneId: this.formValues.timeZoneId,
        },
        false,
        this.notableActivityToEdit
      );

      await this.timeService.syncTimeData();

      this.onClose();
    } catch (e) {
      if (e instanceof NetworkException) {
        this.notificationsService.showToast({ message: 'Your change has been made locally. Please connect to Internet to upload this change.', cssClass: TOAST_CLASS.WARNING });
        this.onClose();
      } else {
        console.log(e);
        this.notificationsService.showToast({ message: 'An error occurred. ' + e, cssClass: TOAST_CLASS.DANGER });
      }
    } finally {
      this.loadingController.dismiss();
    }
  }

  async onDelete() {
    try {
      await this.presentLoading();

      const inputType = await this.deviceInfoService.getInputType();

      await this.timeService.deleteNotableActivity(
        DataChangeEmitWho.Self,
        this.notableActivityToEdit.RecordId,
        {
          InputType: inputType,
        },
        false,
        this.notableActivityToEdit
      );

      await this.timeService.syncTimeData();

      this.onClose();
    } catch (e) {
      if (e instanceof NetworkException) {
        this.notificationsService.showToast({ message: 'Your change has been made locally. Please connect to Internet to upload this change.', cssClass: TOAST_CLASS.WARNING });
        this.onClose();
      } else if (e instanceof ActionValidationException) {
        this.notificationsService.showToast({
          message: 'The date you are modifying has already been verified. Your supervisor will need to return your activity log to you before edits can be made.',
          cssClass: TOAST_CLASS.DANGER,
        });
      } else {
        console.log(e);
        this.notificationsService.showToast({ message: 'An error occurred. ' + e, cssClass: TOAST_CLASS.DANGER });
      }
    } finally {
      this.loadingController.dismiss();
    }
  }
}
